import { createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Context available in all route beforeLoad / loader / component hooks.
 * Passed via <RouterProvider context={…} /> and updated on every render
 * so auth state is always current.
 */
export interface RouterContext {
  queryClient: QueryClient;
  auth: {
    isAuthenticated: boolean;
    /** True while MSAL is processing a redirect or running its startup handshake. */
    isLoading: boolean;
    login: () => Promise<void>;
    logout: () => Promise<void>;
    getAccessToken: (forceRefresh?: boolean) => Promise<string | null>;
    error: string | null;
  };
}

export const router = createRouter({
  routeTree,
  // Disabled: intent-based preload can fire beforeLoad before RouterProvider
  // has supplied the real context, causing `context.auth.isLoading` to throw
  // on the `undefined!` placeholder.  Re-enable once a context guard is added.
  defaultPreload: false,
  // Loaders are considered fresh for 0 ms — always re-run on navigation.
  // Individual queries use their own staleTime via TanStack Query.
  defaultPreloadStaleTime: 0,
  // Placeholder until InnerApp supplies the real values.
  context: {
    queryClient: undefined!,
    auth: undefined!,
  },
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
